import { createSelector, Selector } from "reselect";
import { RootState } from "../../store";
import Dayjs from "common/dayjs";
import { getDatesBetween, getThreeDaysBefore, getUtcOffsetDate } from "common/utils/dateUtils";
import { SHOW_DATE_FORMAT } from "common/constants";
import cloneDeep from "lodash.clonedeep";
import { STATISTIC_BEFORE_EVENT, STATISTIC_DAY } from '../../../components/dashboard/container/DashboardPage.constants';

export const eventEditionSelector: Selector<RootState, RootState["eventEdition"]> = (state) => state.eventEdition;

export const badgesSelector = createSelector(eventEditionSelector, (e) => cloneDeep([...e.badges]));

export const badgeLocationSelector = createSelector(eventEditionSelector, (e) => e.badgeLocation);

export const eventSecretSelector = createSelector(eventEditionSelector, (e) => e.eventSecret);

export const gbsCodeSelector = createSelector(eventEditionSelector, (e) => e.eventEdition!.gbsCode);

export const eventEditionNameSelector = createSelector(eventEditionSelector, (e) => e.eventEdition!.name);

export const badgeOrdersSelector = createSelector(eventEditionSelector, (e) => cloneDeep([...e.orders]));

export const startDateSelector = createSelector(eventEditionSelector, (e) => e.eventEdition!.startDate);

export const endDateSelector = createSelector(eventEditionSelector, (e) => e.eventEdition!.endDate);

export const cmsEventDetailsSelector = createSelector(eventEditionSelector, (e) => e.cmsEventDetails);

export const eventDatesSelector = createSelector(startDateSelector, endDateSelector, (startDate, endDate) => {
  const dates = getDatesBetween(startDate, endDate);

  return dates.map(d => ({ day: Dayjs(d).format(SHOW_DATE_FORMAT) }));
});

export const daysAmountSelector = createSelector(
  eventEditionSelector,
  (e) => {
    const start = Dayjs(e.eventEdition!.startDate);
    const end = Dayjs(e.eventEdition!.endDate);

    return end.diff(start, "day") + 1;
  }
);

export const hasTodayEventEditionSelector = createSelector(startDateSelector, endDateSelector, (startDate, endDate) => {
  const dates = getDatesBetween(startDate, endDate);
  const showBuild = getThreeDaysBefore(dates[0]!);
  const todayIndex = dates.findIndex(d => Dayjs(d).isToday());
  const todayShowIndex = showBuild.findIndex(d => Dayjs(d).isToday());
  if (todayIndex >= 0) {
    return `${STATISTIC_DAY}${todayIndex + 1}`;
  } else if (todayShowIndex >= 0) {
    return `${STATISTIC_BEFORE_EVENT}${todayShowIndex + 1}`;
  } else if (Dayjs(new Date()).isBefore(showBuild[0])) {
    return `${STATISTIC_BEFORE_EVENT}1`;
  } else if (Dayjs(new Date()).isAfter(dates[dates.length - 1]!)) {
    return `${STATISTIC_DAY}${dates.length}`;
  }

  return null;
});

export const eventLocalStartDateSelector = createSelector(
  startDateSelector,
  (startDate) => getUtcOffsetDate(startDate)
);

export const eventLocalEndDateSelector = createSelector(
  endDateSelector,
  (startDate) => getUtcOffsetDate(startDate)
);

export const domainUrlSelector = createSelector(
  eventEditionSelector,
  (e) => `${e.eventEdition!.domain}/${(e.eventEdition!.primaryLocale!).toLowerCase()}/`
);
